import {
  HHChip,
  HHLink,
  HHTypography,
} from '@hinge-health/react-component-library';
import ReadMoreIcon from '@mui/icons-material/ReadMore';
import {
  GridActionsCellItem,
  GridColDef,
  GridRenderCellParams,
  GridRowParams,
} from '@mui/x-data-grid-pro';
import { useMemo } from 'react';
import { routes } from '../../constants/strings/routes';
import { capitalizeFirstLetterAndSpaceSnakeString } from '../../utils/bills-utils';
import { ActiveStatus } from '../../utils/contract-utils';
import { formatDate } from '../../utils/date-helper';
import DisplayContractDetails from '../contract/billing-tool-contract-grid-display';
import { ClientInsurerIndexRow } from './client-insurer-index-utils';

interface UseClientInsurerIndexColumnsProps {
  onViewContract: (ciId: number) => void;
}

export const useClientInsurerIndexColumns = ({
  onViewContract,
}: UseClientInsurerIndexColumnsProps): GridColDef[] =>
  useMemo(
    () => [
      {
        field: 'id',
        headerName: 'ID',
        width: 80,
        sortable: false,
        renderCell: (
          params: GridRenderCellParams<ClientInsurerIndexRow>,
        ): JSX.Element => (
          <HHLink
            href={`${routes.billing.home}/${routes.billing.clientInsurerPath}/${params.value}`}
            target="_blank"
            underline="none"
          >
            {params.value}
          </HHLink>
        ),
      },
      {
        field: 'clientName',
        headerName: 'Client',
        flex: 1,
        minWidth: 150,
        sortable: false,
        renderCell: (
          params: GridRenderCellParams<ClientInsurerIndexRow>,
        ): JSX.Element => (
          <HHLink
            href={`${routes.billing.home}/${routes.billing.clientPath}/${params.row.clientId}`}
            target="_blank"
            underline="none"
          >
            {params.value}
          </HHLink>
        ),
      },
      {
        field: 'insurerName',
        headerName: 'Insurer',
        flex: 1,
        minWidth: 150,
        sortable: false,
      },
      {
        field: 'contractPartnershipName',
        headerName: 'Contract Partnership',
        flex: 1,
        minWidth: 150,
        sortable: false,
        valueFormatter: (params): string => params.value ?? '--',
      },
      {
        field: 'apPartnershipName',
        headerName: 'AP Partnership',
        flex: 1,
        minWidth: 150,
        sortable: false,
        valueFormatter: (params): string => params.value ?? '--',
      },
      {
        field: 'effectiveDate',
        headerName: 'Effective Date',
        width: 130,
        sortable: false,
        valueFormatter: (params): string =>
          params.value ? formatDate(params.value) : '--',
      },
      {
        field: 'contractType',
        headerName: 'Contract Type',
        flex: 1,
        minWidth: 130,
        sortable: false,
        valueFormatter: (params): string => params.value ?? '--',
      },
      {
        field: 'packageDetails',
        headerName: 'Package Details',
        flex: 1.2,
        minWidth: 160,
        sortable: false,
        renderCell: (
          params: GridRenderCellParams<ClientInsurerIndexRow>,
        ): JSX.Element => {
          const details = params.value;
          if (!details) {
            return <HHTypography hhVariant="caption">--</HHTypography>;
          }
          return (
            <DisplayContractDetails
              chronicPrice={details.chronicPrice}
              acutePrice={details.acutePrice}
              inPersonVisitPrice={details.inPersonVisitPrice}
              contractPackages={details.contractPackages}
              packageEnabled={details.packageEnabled}
              currency={details.currency}
              displayBillingTypeOverride={true}
            />
          );
        },
      },
      {
        field: 'contractDateRange',
        headerName: 'Contract Dates',
        flex: 1,
        minWidth: 180,
        sortable: false,
        valueFormatter: (params): string => params.value ?? '--',
      },
      {
        field: 'billingType',
        headerName: 'Billing Type',
        width: 140,
        sortable: false,
        renderCell: (
          params: GridRenderCellParams<ClientInsurerIndexRow>,
        ): JSX.Element => {
          const billingType = params.value as string | null;
          if (!billingType) {
            return <HHTypography hhVariant="caption">--</HHTypography>;
          }
          const formattedBillingType =
            capitalizeFirstLetterAndSpaceSnakeString(billingType);
          return (
            <HHChip
              hhVariant="variant-bypass"
              color="info"
              size="small"
              label={formattedBillingType}
              title={formattedBillingType}
            />
          );
        },
      },
      {
        field: 'apBillingType',
        headerName: 'AP Billing Type',
        width: 140,
        sortable: false,
        renderCell: (
          params: GridRenderCellParams<ClientInsurerIndexRow>,
        ): JSX.Element => {
          const billingType = params.value as string | null;
          if (!billingType) {
            return <HHTypography hhVariant="caption">--</HHTypography>;
          }
          const formattedBillingType =
            capitalizeFirstLetterAndSpaceSnakeString(billingType);
          return (
            <HHChip
              hhVariant="variant-bypass"
              color="info"
              size="small"
              label={formattedBillingType}
              title={formattedBillingType}
            />
          );
        },
      },
      {
        field: 'coreContractRule',
        headerName: 'Core Contract Rule',
        flex: 1,
        minWidth: 140,
        sortable: false,
        valueFormatter: (params): string => params.value ?? '--',
      },
      {
        field: 'actions',
        headerName: 'Actions',
        type: 'actions',
        width: 80,
        sortable: false,
        getActions: (
          params: GridRowParams<ClientInsurerIndexRow>,
        ): JSX.Element[] => {
          const hasActiveContract =
            params.row.contractStatus === ActiveStatus.Active;
          return [
            <GridActionsCellItem
              key={`${params.id}-view-contract`}
              label="View Contract"
              icon={
                <ReadMoreIcon
                  color={hasActiveContract ? 'primary' : 'disabled'}
                />
              }
              onClick={(): void => onViewContract(params.row.id)}
              disabled={!hasActiveContract}
              showInMenu={false}
            />,
          ];
        },
      },
    ],
    [onViewContract],
  );
